import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../../lib/api';

/**
 * 单位快速新建弹窗——从产品表单就地建单位（用户实测反馈：新公司单位字典为空，
 * 逼着用户中断建产品流程绕道设置页）。与 settings/uoms-panel.tsx 打同一个
 * POST /uoms（settings.manage 门槛在后端 Guard，调用方负责用 useCan 决定
 * 是否渲染入口）。名称留空时用代码兜底：uom.name_key 展示端本来就有
 * `t(nameKey, code)` 的回退。
 */
export function UomCreateModal({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void;
  readonly onCreated: (id: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const create = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch<{ id: string }>('/uoms', {
        method: 'POST',
        body: {
          code: code.trim(),
          nameKey: name.trim() || code.trim(),
          symbol: symbol.trim() || undefined,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['uoms'] });
      onCreated(res.id);
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={(e) => void create(e)}>
        <h2>{t('uom.create')}</h2>
        <label className="field">
          <span>{t('uom.colCode')}</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            autoFocus
            maxLength={50}
            placeholder="箱 / box"
          />
        </label>
        <label className="field">
          <span>{t('uom.colName')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            placeholder={code.trim() || undefined}
          />
          <span className="field-hint">{t('uom.nameOptionalHint')}</span>
        </label>
        <label className="field">
          <span>{t('uom.colSymbol')}</span>
          <input
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            maxLength={20}
            placeholder="pc"
          />
        </label>
        {error && <p className="field-error">{error}</p>}
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('app.cancel')}
          </button>
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {t('uom.create')}
          </button>
        </div>
      </form>
    </div>
  );
}
